import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAppStore } from '@/stores/appStore'
import { useFocusedPerson } from '@/hooks/useFocusedPerson'
import { ageString } from '@/lib/format'
import type { EventRow } from '@/lib/types'

/**
 * Weekly report — a print-optimized page you hit before a pediatric visit.
 * Route: /reports/weekly?days=7 (default) — cmd/ctrl-P to save as PDF.
 *
 * Deliberately spare: this is a document, not an app screen. Print media styles
 * kick tab bar / header chrome out of the way. Landscape or portrait both work.
 */

interface WeeklyBucket {
  date: string          // YYYY-MM-DD
  dayLabel: string      // 'Sun Sep 7'
  feeds: number
  feedVolumeMl: number
  wetDiapers: number
  dirtyDiapers: number
  sleepMinutes: number
  medications: Array<{ name: string; at: string }>
  notes: string[]
  growth: Array<{ type: string; value: number; unit: string }>
}

interface Report {
  personName: string
  ageString: string
  familyName: string
  from: string
  to: string
  days: WeeklyBucket[]
  totals: {
    feeds: number
    feedVolumeMl: number
    wetDiapers: number
    dirtyDiapers: number
    sleepHours: number
    medications: number
  }
  generatedAt: string
}

export function WeeklyReportScreen() {
  const [params] = useSearchParams()
  const days = Math.max(1, Math.min(90, parseInt(params.get('days') ?? '7', 10)))
  const familyId = useAppStore((s) => s.familyId)
  const person = useFocusedPerson()

  const [report, setReport] = useState<Report | null>(null)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    if (!familyId || !person) return
    let cancelled = false
    ;(async () => {
      try {
        const r = await buildReport(familyId, person, days)
        if (!cancelled) setReport(r)
      } catch (e) {
        if (!cancelled) setErr(e instanceof Error ? e.message : String(e))
      }
    })()
    return () => { cancelled = true }
  }, [familyId, person, days])

  if (!familyId || !person) {
    return <div style={{ padding: 40, fontFamily: 'system-ui', textAlign: 'center' }}>Sign in first.</div>
  }
  if (err) {
    return <div style={{ padding: 40, fontFamily: 'system-ui', color: '#B94E3F' }}>Report failed: {err}</div>
  }
  if (!report) {
    return <div style={{ padding: 40, fontFamily: 'system-ui', color: '#8B968F' }}>Building report…</div>
  }

  return (
    <>
      {/* Print styles + tab-bar hide */}
      <style>{`
        @media print {
          nav, [aria-label="Primary"], .no-print { display: none !important; }
          body { background: white !important; }
          .report { color: black !important; }
          .report * { color: black !important; }
        }
      `}</style>

      <div className="report" style={{
        maxWidth: 900,
        margin: '0 auto',
        padding: '32px 40px',
        background: 'white',
        color: '#111',
        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", Inter, sans-serif',
        lineHeight: 1.5,
        minHeight: '100vh',
      }}>
        {/* Header */}
        <div style={{ borderBottom: '2px solid #111', paddingBottom: 16, marginBottom: 24 }}>
          <div style={{ fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#666' }}>
            Wren — Weekly Pediatric Summary
          </div>
          <h1 style={{ margin: '4px 0 0 0', fontFamily: 'Instrument Serif, Georgia, serif', fontStyle: 'italic', fontSize: 40, lineHeight: 1.1 }}>
            {report.personName}
          </h1>
          <div style={{ fontSize: 13, color: '#666', marginTop: 6 }}>
            {report.ageString ? `${report.ageString} · ` : ''}
            {report.from} → {report.to} · generated {new Date(report.generatedAt).toLocaleString()}
          </div>
        </div>

        {/* Totals band */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 12, marginBottom: 32 }}>
          <Metric label="Feeds" value={report.totals.feeds} />
          <Metric label="Volume" value={`${report.totals.feedVolumeMl} mL`} />
          <Metric label="Wet diapers" value={report.totals.wetDiapers} />
          <Metric label="Dirty diapers" value={report.totals.dirtyDiapers} />
          <Metric label="Sleep" value={`${report.totals.sleepHours.toFixed(1)}h`} />
          <Metric label="Doses" value={report.totals.medications} />
        </div>

        {/* Day-by-day breakdown */}
        <h2 style={{ fontSize: 12, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#666', margin: '0 0 12px 0', fontWeight: 700 }}>
          Day by day
        </h2>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginBottom: 32 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #ccc', textAlign: 'left' }}>
              <th style={{ padding: '6px 4px' }}>Day</th>
              <th style={{ padding: '6px 4px', textAlign: 'right' }}>Feeds</th>
              <th style={{ padding: '6px 4px', textAlign: 'right' }}>Volume</th>
              <th style={{ padding: '6px 4px', textAlign: 'right' }}>Wet</th>
              <th style={{ padding: '6px 4px', textAlign: 'right' }}>Dirty</th>
              <th style={{ padding: '6px 4px', textAlign: 'right' }}>Sleep</th>
              <th style={{ padding: '6px 4px' }}>Medications</th>
            </tr>
          </thead>
          <tbody>
            {report.days.map((d) => (
              <tr key={d.date} style={{ borderBottom: '1px solid #eee' }}>
                <td style={{ padding: '6px 4px', fontWeight: 500 }}>{d.dayLabel}</td>
                <td style={{ padding: '6px 4px', textAlign: 'right' }}>{d.feeds}</td>
                <td style={{ padding: '6px 4px', textAlign: 'right' }}>{d.feedVolumeMl} mL</td>
                <td style={{ padding: '6px 4px', textAlign: 'right' }}>{d.wetDiapers}</td>
                <td style={{ padding: '6px 4px', textAlign: 'right' }}>{d.dirtyDiapers}</td>
                <td style={{ padding: '6px 4px', textAlign: 'right' }}>{(d.sleepMinutes / 60).toFixed(1)}h</td>
                <td style={{ padding: '6px 4px', fontSize: 11 }}>
                  {d.medications.length === 0 ? '—' : d.medications.map((m) => `${m.name} @ ${new Date(m.at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`).join('; ')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Growth measurements section */}
        {report.days.some((d) => d.growth.length > 0) && (
          <>
            <h2 style={{ fontSize: 12, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#666', margin: '0 0 12px 0', fontWeight: 700 }}>
              Growth measurements
            </h2>
            <ul style={{ fontSize: 13, margin: '0 0 32px 0', paddingLeft: 20 }}>
              {report.days.flatMap((d) => d.growth.map((g, i) => (
                <li key={`${d.date}-${i}`} style={{ marginBottom: 4 }}>
                  <strong>{d.dayLabel}:</strong> {g.type} — {g.value} {g.unit}
                </li>
              )))}
            </ul>
          </>
        )}

        {/* Notes section */}
        {report.days.some((d) => d.notes.length > 0) && (
          <>
            <h2 style={{ fontSize: 12, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#666', margin: '0 0 12px 0', fontWeight: 700 }}>
              Notes
            </h2>
            {report.days.filter((d) => d.notes.length > 0).map((d) => (
              <div key={d.date} style={{ marginBottom: 12, fontSize: 13 }}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>{d.dayLabel}</div>
                {d.notes.map((n, i) => (
                  <div key={i} style={{ marginLeft: 12, marginBottom: 2, color: '#333' }}>· {n}</div>
                ))}
              </div>
            ))}
          </>
        )}

        {/* Footer */}
        <div style={{
          marginTop: 40,
          paddingTop: 16,
          borderTop: '1px solid #ccc',
          fontSize: 10,
          color: '#999',
          fontStyle: 'italic',
        }}>
          Generated by Wren · {report.familyName} · every entry is a real event recorded by a caregiver.
          Corrections (superseded events) are excluded from these totals so the numbers reflect current truth.
        </div>

        {/* On-screen only: print button */}
        <div className="no-print" style={{
          position: 'fixed',
          bottom: 24,
          right: 24,
        }}>
          <button
            type="button"
            onClick={() => window.print()}
            style={{
              padding: '12px 24px',
              borderRadius: 999,
              background: '#8AB89F',
              color: '#0B0F0E',
              fontWeight: 600,
              border: 'none',
              cursor: 'pointer',
              fontSize: 15,
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            }}
          >
            Print / Save as PDF
          </button>
        </div>
      </div>
    </>
  )
}

function Metric({ label, value }: { label: string; value: number | string }) {
  return (
    <div>
      <div style={{ fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#666' }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, marginTop: 2 }}>{value}</div>
    </div>
  )
}

async function buildReport(familyId: string, person: import('@/lib/types').Person, days: number): Promise<Report> {
  const now = new Date()
  const fromMs = now.getTime() - days * 86400000
  const fromDate = new Date(fromMs)
  fromDate.setHours(0, 0, 0, 0)

  // Fetch every event + measurement for the window
  const [eventsRes, measRes, familyRes] = await Promise.all([
    supabase
      .from('events')
      .select('*')
      .eq('family_id', familyId)
      .eq('person_id', person.id)
      .gte('occurred_at', fromDate.toISOString())
      .order('occurred_at', { ascending: true }),
    supabase
      .from('measurements')
      .select('measurement_type, value, unit, measured_at')
      .eq('family_id', familyId)
      .eq('person_id', person.id)
      .gte('measured_at', fromDate.toISOString()),
    supabase.from('families').select('display_name').eq('id', familyId).maybeSingle(),
  ])
  if (eventsRes.error) throw new Error(eventsRes.error.message)
  const events = (eventsRes.data ?? []) as EventRow[]

  // Filter out superseded events
  const supersededIds = new Set(events.map((e) => e.supersedes_event_id).filter((x): x is string => !!x))
  const active = events.filter((e) => !supersededIds.has(e.id))

  // Bucket by day
  const buckets = new Map<string, WeeklyBucket>()
  for (let d = 0; d < days; d++) {
    const day = new Date(fromDate.getTime() + d * 86400000)
    const key = day.toISOString().slice(0, 10)
    buckets.set(key, {
      date: key,
      dayLabel: day.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' }),
      feeds: 0,
      feedVolumeMl: 0,
      wetDiapers: 0,
      dirtyDiapers: 0,
      sleepMinutes: 0,
      medications: [],
      notes: [],
      growth: [],
    })
  }

  // Sleep pairing needs sequential state
  let openSleep: { at: string } | null = null

  for (const e of active) {
    const key = new Date(e.occurred_at).toISOString().slice(0, 10)
    const b = buckets.get(key)
    if (!b) continue
    const p = e.payload as Record<string, unknown>
    switch (e.event_type) {
      case 'early_care.feeding.recorded':
      case 'early_care.feeding.corrected':
        b.feeds++
        b.feedVolumeMl += Number(p.volume_ml ?? 0)
        break
      case 'early_care.diaper.recorded':
      case 'early_care.diaper.corrected': {
        const dt = p.diaper_type as string
        if (dt === 'wet' || dt === 'both') b.wetDiapers++
        if (dt === 'dirty' || dt === 'both') b.dirtyDiapers++
        break
      }
      case 'early_care.sleep.started':
        openSleep = { at: e.occurred_at }
        break
      case 'early_care.sleep.ended':
        if (openSleep) {
          const startMs = new Date(openSleep.at).getTime()
          const endMs = new Date(e.occurred_at).getTime()
          const mins = Math.max(0, Math.round((endMs - startMs) / 60000))
          // Credit to the bucket the sleep STARTED in
          const startKey = new Date(openSleep.at).toISOString().slice(0, 10)
          const startBucket = buckets.get(startKey)
          if (startBucket) startBucket.sleepMinutes += mins
          openSleep = null
        }
        break
      case 'early_care.medication.recorded':
      case 'early_care.medication.corrected':
        b.medications.push({ name: (p.medication_name as string) ?? 'unknown', at: e.occurred_at })
        break
      case 'note.created': {
        const text = (p.text as string) ?? ''
        if (text.trim()) b.notes.push(text)
        break
      }
    }
  }

  // Bucket measurements
  for (const m of measRes.data ?? []) {
    const key = new Date(m.measured_at as string).toISOString().slice(0, 10)
    const b = buckets.get(key)
    if (b) b.growth.push({ type: m.measurement_type as string, value: m.value as number, unit: m.unit as string })
  }

  const daysArr = Array.from(buckets.values())
  const totals = daysArr.reduce(
    (acc, d) => ({
      feeds: acc.feeds + d.feeds,
      feedVolumeMl: acc.feedVolumeMl + d.feedVolumeMl,
      wetDiapers: acc.wetDiapers + d.wetDiapers,
      dirtyDiapers: acc.dirtyDiapers + d.dirtyDiapers,
      sleepHours: acc.sleepHours + d.sleepMinutes / 60,
      medications: acc.medications + d.medications.length,
    }),
    { feeds: 0, feedVolumeMl: 0, wetDiapers: 0, dirtyDiapers: 0, sleepHours: 0, medications: 0 },
  )

  return {
    personName: person.display_name,
    ageString: person.date_of_birth ? ageString(person.date_of_birth) : '',
    familyName: (familyRes.data?.display_name as string) ?? 'Family',
    from: fromDate.toLocaleDateString([], { month: 'short', day: 'numeric' }),
    to: now.toLocaleDateString([], { month: 'short', day: 'numeric' }),
    days: daysArr,
    totals,
    generatedAt: new Date().toISOString(),
  }
}
